import { buildAlgorithm } from './TTVRAlgorithmBuilder';
import type { ImageParams, ImporterConfig, ImportEntitiy } from '@/engine/Serialization/IImporter';

function _getImagePhaseParams(): Map<string, ImageParams> {
  const ret = new Map<string, ImageParams>();
  ret.set('Heart', {
    perPhaseInfo: [
      {
        paramName: 'rvVolume',
        algoName: 'var_rv_volume'
      },
      {
        paramName: 'annulusSize',
        algoName: 'var_landing_zone_area_perimeter'
      }
    ],
    perKindGetter: (perPhaseParams: Map<string, Array<number>>): Map<string, number> => {
      const volumes = perPhaseParams.get('rvVolume');
      const sizes = perPhaseParams.get('annulusSize');
      if (!volumes || !sizes) throw new Error('Logic error');

      const idxMin = volumes.indexOf(Math.min(...volumes));
      if (idxMin < 0 || idxMin >= volumes.length) throw new Error('Logic error');

      const idxMax = volumes.indexOf(Math.max(...volumes));
      if (idxMax < 0 || idxMax >= volumes.length) throw new Error('Logic error');

      const endDiaSize = sizes.at(idxMax);
      if (endDiaSize === undefined) throw new Error('Logic error');

      const ret = new Map<string, number>();
      ret.set('endSystoleIndex', idxMin);
      ret.set('endDiastoleIndex', idxMax);
      ret.set('endDiastoleAnnulusSize', endDiaSize);
      return ret;
    },
    initialIndexParamName: 'endDiastoleIndex',
    perKindInfo: [
      {
        paramName: 'endDiastoleAnnulusSize',
        algoName: 'var_annulus_perimeter_for_sizing_provided'
      }
    ]
  });
  return ret;
}

function _GetPhaseProgress(label: string): number | undefined {
  const match = label.match(/^(\d+)%/)?.at(1);
  if (match !== undefined) {
    return parseInt(match);
  }
  return undefined;
}

function _phaseWeightResolver(kind: string, label: string): number | undefined {
  if (kind != 'Heart') return undefined;
  return _GetPhaseProgress(label);
}

export function getConfig(): ImporterConfig {
  const points: Array<ImportEntitiy> = [
    // Main step points
    {
      importName: 'svc1_1',
      algoName: 'pt_svc11_imported',
      isOptionalEntity: false
      // Phase-independent
    },
    {
      importName: 'svc1_2',
      algoName: 'pt_svc12_imported',
      isOptionalEntity: false
      // Phase-independent
    },
    {
      importName: 'svc1_3',
      algoName: 'pt_svc13_imported',
      isOptionalEntity: false
      // Phase-independent
    },
    {
      importName: 'ivc1_1',
      algoName: 'pt_ivc11_imported',
      isOptionalEntity: false
      // Phase-independent
    },
    {
      importName: 'ivc1_2',
      algoName: 'pt_ivc12_imported',
      isOptionalEntity: false
      // Phase-independent
    },
    {
      importName: 'ivc1_3',
      algoName: 'pt_ivc13_imported',
      isOptionalEntity: false
      // Phase-independent
    },
    // Delivery step points
    {
      importName: 'rfv',
      algoName: 'pt_right_femoral_vessel_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Body'
    },
    {
      importName: 'lfv',
      algoName: 'pt_left_femoral_vessel_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Body'
    },
    {
      importName: 'clr',
      algoName: 'pt_centerline_reference_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Body'
    }
  ];

  const meshes: Array<ImportEntitiy> = [
    // Main step meshes
    {
      importName: 'pulmonary artery',
      algoName: 'pulmonary_artery.descriptor',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'right atrium',
      algoName: 'right_atrium.descriptor',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'right ventricle',
      algoName: 'right_ventricle.descriptor',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'right heart',
      algoName: 'right_heart.descriptor',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'left atrium',
      algoName: 'left_atrium.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'left ventricle',
      algoName: 'left_ventricle.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'aorta',
      algoName: 'aorta.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'additional device',
      algoName: 'additional_device.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'additional structure',
      algoName: 'additional_structure.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'calcification',
      algoName: 'calcification.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Heart'
    },
    // Delivery step entities
    {
      importName: 'veins',
      algoName: 'vessel.descriptor',
      isOptionalEntity: false,
      imagePhaseKind: 'Body'
    },
    {
      importName: 'bones',
      algoName: 'bones.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Body'
    },
    {
      importName: 'arteries & kidneys',
      algoName: 'arteries_kidneys.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Body'
    },
    {
      importName: 'filter',
      algoName: 'filter.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Body'
    },
    {
      importName: 'hardware',
      algoName: 'hardware.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Body'
    }
  ];

  const splines: Array<ImportEntitiy> = [
    // Main step splines
    {
      importName: 'landing zone',
      algoName: 'pts_landing_zone_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'landing zone', // duplicate for commissure points distribution
      algoName: 'pts_commissure_spline_imported',
      isOptionalEntity: false
      // Phase-independent
    },
    {
      importName: 'eustachian ridge',
      algoName: 'pts_eustachian_ridge_imported',
      isOptionalEntity: true,
      imagePhaseKind: 'Heart'
    }
  ];

  return {
    meshes: meshes,
    points: points,
    splines: splines,
    polylinesVectors: [],
    algorithmBuilder: buildAlgorithm,
    imageParams: _getImagePhaseParams(),
    phaseWeightResolver: _phaseWeightResolver,
    optionalImagePhaseKinds: ['Body']
  };
}
